import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { PopUpDialogComponent } from '../pop-up-dialog/pop-up-dialog.component';
import { TextInputData, TextPromptDialogComponent } from '../text-prompt-dialog/text-prompt-dialog.component';
import { ComponentType } from '@angular/cdk/overlay';

export interface PopUpDialogParams {
  title?: string;
  message?: string;
  icon?: string;
  confirm?: string;
  cancel?: string;
  displayCancel?: boolean;
  messageParams?: Record<string, unknown>;
  templateRef?: TemplateRef<unknown>;
  closeOnConfirm?: boolean;
  hidePopupLayoutElements?: boolean;
  alwaysUnlockConfirm?: boolean;
}

@Injectable()
export class DialogService {
  private ref: MatDialogRef<unknown>;

  constructor(private readonly dialog: MatDialog) {}

  closeAll(): void {
    this.dialog.closeAll();
  }

  close(value?: unknown): void {
    this.ref.close(value);
  }

  open<T = unknown>(component: ComponentType<unknown>, config?: MatDialogConfig<unknown>): Promise<T> {
    this.ref = this.dialog.open(component, config);
    return firstValueFrom(this.ref.afterClosed());
  }

  openTemplate<T = unknown>(template: TemplateRef<unknown>, config?: MatDialogConfig<unknown>): Promise<T> {
    this.ref = this.dialog.open(template, config);
    return firstValueFrom(this.ref.afterClosed());
  }

  show(width: string, params: PopUpDialogParams, extraConfigs?: MatDialogConfig): Promise<boolean> {
    this.ref = this.dialog.open<PopUpDialogComponent, PopUpDialogParams, boolean>(PopUpDialogComponent, {
      width: width,
      data: params,
      ...extraConfigs,
    });

    return firstValueFrom(this.ref.afterClosed());
  }

  showDelete(messageKey = 'uix.popUpDialog.message', messageParams?: Record<string, any>): Promise<boolean> {
    return this.show('500px', {
      title: 'commons.titles.confirm',
      message: messageKey,
      messageParams: messageParams,
      icon: 'delete',
      confirm: 'commons.btn.delete',
    });
  }

  showConfirm(messageKey = 'uix.popUpDialog.message', messageParams?: Record<string, any>): Promise<boolean> {
    return this.show('500px', {
      title: 'commons.titles.confirm',
      message: messageKey,
      messageParams: messageParams,
      icon: 'check',
      confirm: 'commons.btn.confirm',
    });
  }

  showTextInput(data: TextInputData): Promise<string | null> {
    this.ref = this.dialog.open<TextPromptDialogComponent, TextInputData, string | null>(TextPromptDialogComponent, {
      width: '500px',
      data: {
        ...data,
        required: data.required ?? true,
      },
    });

    return firstValueFrom(this.ref.afterClosed()).then((result) => result ?? null);
  }
}
